import { useState, useEffect, useCallback } from 'react';

/**
 * Hook for å lagre skjemadata i localStorage
 * Data bevares selv om brukeren navigerer bort eller gjør feil
 * 
 * @param {string} key - Unik nøkkel for localStorage
 * @param {object} initialData - Standard skjemadata
 * @param {object} options - Valgfrie innstillinger
 * @returns {[object, function, function]} - [data, setData, clearData]
 */
const useFormPersistence = (key, initialData, options = {}) => {
  const {
    debounceMs = 500,
    excludeFields = [], // Felt som ikke skal lagres (f.eks. passord)
  } = options;

  const storageKey = `minitask_form_${key}`;

  // Initialiser state med lagrede data eller initialData
  const [data, setDataState] = useState(() => {
    try {
      const stored = localStorage.getItem(storageKey);
      if (stored) {
        const parsed = JSON.parse(stored);
        // Merge med initialData for å sikre at nye felt er med
        return { ...initialData, ...parsed };
      }
    } catch (error) {
      console.warn('Kunne ikke lese lagret skjemadata:', error);
    }
    return initialData;
  });

  // Debounced lagring til localStorage
  useEffect(() => {
    const timer = setTimeout(() => {
      try {
        // Fjern ekskluderte felt før lagring
        const dataToStore = { ...data };
        excludeFields.forEach(field => {
          delete dataToStore[field];
        });
        localStorage.setItem(storageKey, JSON.stringify(dataToStore));
      } catch (error) {
        console.warn('Kunne ikke lagre skjemadata:', error);
      }
    }, debounceMs);

    return () => clearTimeout(timer);
  }, [data, storageKey, debounceMs, excludeFields]);

  // Funksjon for å oppdatere data
  const setData = useCallback((newData) => {
    if (typeof newData === 'function') {
      setDataState(prev => newData(prev));
    } else {
      setDataState(prev => ({ ...prev, ...newData }));
    }
  }, []);

  // Funksjon for å tømme lagret data
  const clearData = useCallback(() => {
    try {
      localStorage.removeItem(storageKey);
      setDataState(initialData);
    } catch (error) {
      console.warn('Kunne ikke slette skjemadata:', error);
    }
  }, [storageKey, initialData]);

  return [data, setData, clearData];
};

export default useFormPersistence;

/**
 * Hook for å lagre enkeltverdi i localStorage
 */
export const useLocalStorage = (key, initialValue) => {
  const storageKey = `minitask_${key}`;

  const [value, setValue] = useState(() => {
    try {
      const stored = localStorage.getItem(storageKey);
      return stored ? JSON.parse(stored) : initialValue;
    } catch {
      return initialValue;
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(storageKey, JSON.stringify(value));
    } catch (error) {
      console.warn('Kunne ikke lagre til localStorage:', error);
    }
  }, [storageKey, value]);

  const removeValue = useCallback(() => {
    try {
      localStorage.removeItem(storageKey);
      setValue(initialValue);
    } catch (error) {
      console.warn('Kunne ikke slette fra localStorage:', error);
    }
  }, [storageKey, initialValue]);

  return [value, setValue, removeValue];
};
